import { ACCESS_DENIED_PATH } from '@gestion-permisos/contracts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type RouteObject, RouterProvider, useRouteError } from 'react-router-dom';
import { ApiProvider } from './api/api-context.js';
import type { ApiClient } from './api/client.js';
import { safeReturnTo } from './api/login.js';
import { ApiErrorView } from './components/api-error-view.js';
import { AppShell } from './components/app-shell.js';
import { AdminAreasPage } from './pages/admin-areas-page.js';
import { AdminAuditPage } from './pages/admin-audit-page.js';
import { AdminCalendarPage } from './pages/admin-calendar-page.js';
import { AdminEmployeePage } from './pages/admin-employee-page.js';
import { AdminEmployeesPage } from './pages/admin-employees-page.js';
import { AdminHierarchyPage } from './pages/admin-hierarchy-page.js';
import { AdminRequestPage } from './pages/admin-request-page.js';
import { AdminRequestsPage } from './pages/admin-requests-page.js';
import { AdminIndexPage } from './pages/admin-index-page.js';
import { AdminTypesPage } from './pages/admin-types-page.js';
import { AnalyticsPage } from './pages/analytics-page.js';
import { ApprovalReviewPage } from './pages/approval-review-page.js';
import { ApprovalsPage } from './pages/approvals-page.js';
import { HomePage } from './pages/home-page.js';
import { ManagerPanelPage } from './pages/manager-panel-page.js';
import { MyRequestsPage } from './pages/my-requests-page.js';
import { EditRequestPage, NewRequestPage } from './pages/request-form-pages.js';
import { AccessDeniedPage, NotFoundPage, SessionClosedPage, SessionExpiredPage } from './pages/public-pages.js';
import { RequestDetailPage } from './pages/request-detail-page.js';
import { TeamMemberPage } from './pages/team-member-page.js';
import { TeamPage } from './pages/team-page.js';
import { SessionGate } from './session/session.js';

/** Un error lanzado al mostrar una pantalla autenticada se presenta como los de la API, sin trazas. */
function RouteErrorView() {
  return <ApiErrorView error={useRouteError()} />;
}

/**
 * Rutas (plan 11 §5 y §7): pantallas públicas sin llamadas a la API y pantallas autenticadas dentro de la
 * estructura con navegación. `shellRoutes` permite añadir pantallas autenticadas (las pruebas lo usan
 * para simular respuestas 403 y 404 de la API dentro de la estructura).
 */
export function createRoutes(shellRoutes: RouteObject[] = []): RouteObject[] {
  return [
    { path: '/sesion-cerrada', element: <SessionClosedPage /> },
    { path: '/sesion-expirada', element: <SessionExpiredPage /> },
    { path: ACCESS_DENIED_PATH, element: <AccessDeniedPage /> },
    {
      element: <SessionGate />,
      children: [
        {
          element: <AppShell />,
          errorElement: <RouteErrorView />,
          children: [
            { index: true, element: <HomePage /> },
            // W2: solicitudes propias, solo lectura. `solicitudes/nueva` (estática) tiene prioridad sobre `:id`.
            { path: 'mis-solicitudes', element: <MyRequestsPage /> },
            { path: 'solicitudes/:id', element: <RequestDetailPage /> },
            // W3: alta y edición del borrador propio.
            { path: 'solicitudes/nueva', element: <NewRequestPage /> },
            { path: 'solicitudes/:id/editar', element: <EditRequestPage /> },
            // W4: bandeja y revisión del aprobador (decidir siempre con POST; sin rutas de enlaces de correo).
            { path: 'aprobaciones', element: <ApprovalsPage /> },
            { path: 'aprobaciones/:id', element: <ApprovalReviewPage /> },
            // W5: equipo directo, sus solicitudes (al detalle común) y la ficha de cada subordinado.
            { path: 'mi-equipo', element: <TeamPage /> },
            { path: 'mi-equipo/:id', element: <TeamMemberPage /> },
            // W6: panel del responsable (contadores) y analítica con la exportación a Excel.
            { path: 'panel-responsable', element: <ManagerPanelPage /> },
            { path: 'analitica', element: <AnalyticsPage /> },
            // W7: catálogos de administración (empleados, jerarquía y roles llegan en W8).
            { path: 'administracion', element: <AdminIndexPage /> },
            { path: 'administracion/areas', element: <AdminAreasPage /> },
            { path: 'administracion/tipos', element: <AdminTypesPage /> },
            { path: 'administracion/calendario', element: <AdminCalendarPage /> },
            // W8: personas, jerarquía, roles y revocación de sesiones.
            { path: 'administracion/empleados', element: <AdminEmployeesPage /> },
            { path: 'administracion/empleados/:id', element: <AdminEmployeePage /> },
            { path: 'administracion/jerarquia', element: <AdminHierarchyPage /> },
            // W9: listado y detalle administrativo de solicitudes.
            { path: 'administracion/solicitudes', element: <AdminRequestsPage /> },
            { path: 'administracion/solicitudes/:id', element: <AdminRequestPage /> },
            // W10: auditoría (solo lectura).
            { path: 'administracion/auditoria', element: <AdminAuditPage /> },
            ...shellRoutes,
          ],
        },
      ],
    },
    { path: '*', element: <NotFoundPage /> },
  ];
}

export const routes: RouteObject[] = createRoutes();

/** Ruta de la pantalla de sesión expirada, con la ruta (relativa) a la que volver. */
export function sessionExpiredPath(returnTo: string): string {
  return `/sesion-expirada?volver=${encodeURIComponent(safeReturnTo(returnTo))}`;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      // Sin reintentos automáticos: un 401 o 403 no debe repetirse y los errores se muestran tal cual.
      queries: { retry: false, refetchOnWindowFocus: false, staleTime: 60_000 },
      mutations: { retry: false },
    },
  });
}

type Router = Parameters<typeof RouterProvider>[0]['router'];

export function WebApp({ router, api, queryClient }: { router: Router; api: ApiClient; queryClient: QueryClient }) {
  return (
    <ApiProvider client={api}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ApiProvider>
  );
}
